import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../services/api.js";

export default function CreateOrderModal({
  open,
  onClose,
  cust_id,
  token,
  onSuccess,
}) {
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [includeTax, setIncludeTax] = useState(true); // State baru untuk tax
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const taxRate = 11; // Hardcode sesuai backend

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // ===============================
  // FETCH PRODUCTS
  // ===============================
  const fetchData = async () => {
    try {
      const res = await api.get(`/products`, {
        headers: { Authorization: token },
      });
      setProducts(res.data.data || []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch products", "error");
    }
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  if (!open) return null;

  // ===============================
  // HANDLERS
  // ===============================
  const addItem = (product) => {
    if (selectedItems.some((i) => i.product_id === product.id)) {
      return Swal.fire("Info", "Produk sudah ditambahkan", "info");
    }

    setSelectedItems([
      ...selectedItems,
      {
        product_id: product.id,
        name: product.name,
        price: Number(product.price),
        qty: 1,
      },
    ]);
  };

  const updateQty = (product_id, qty) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.product_id === product_id
          ? { ...item, qty: Math.max(1, qty) }
          : item
      )
    );
  };

  const removeItem = (product_id) => {
    setSelectedItems(selectedItems.filter((i) => i.product_id !== product_id));
  };

  // ===============================
  // CALCULATIONS
  // ===============================
  const subtotal = selectedItems.reduce((sum, i) => sum + i.price * i.qty, 0);
  const taxAmount = includeTax ? (subtotal * taxRate) / 100 : 0;
  const total = subtotal + taxAmount;

  // ===============================
  // SUBMIT
  // ===============================
  const handleSubmit = async () => {
    if (!selectedItems.length) {
      return Swal.fire("Warning", "Pilih minimal 1 produk", "warning");
    }

    setLoading(true);
    try {
      await api.post(
        "/orders",
        {
          customer_id: cust_id,
          items: selectedItems.map((i) => ({
            product_id: i.product_id,
            qty: i.qty,
            price: i.price,
          })),
          notes,
          include_tax: includeTax, // Kirim parameter tax ke backend
        },
        {
          headers: { Authorization: token },
        }
      );

      Swal.fire("Success", "Order berhasil dibuat", "success");

      setSelectedItems([]);
      setNotes("");
      setIncludeTax(false);
      onClose();
      onSuccess && onSuccess();
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Gagal membuat order",
        "error"
      );
    }
    setLoading(false);
  };

  const updatePrice = (product_id, price) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.product_id === product_id
          ? { ...item, price: Math.max(0, price) }
          : item
      )
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* OVERLAY */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* MODAL */}
      <div className="relative w-full max-w-3xl bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 overflow-y-auto max-h-[90vh]">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold dark:text-white">Create Order</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {/* PRODUCT SEARCH */}
        <div className="mb-5 relative">
          <label className="block text-sm font-semibold mb-1 dark:text-gray-300">
            Search Product
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type product name..."
            className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
          />

          {search && filteredProducts.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow max-h-60 overflow-y-auto">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => {
                    addItem(product);
                    setSearch("");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                  <div className="font-medium dark:text-gray-200">
                    {product.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    Rp {Number(product.price).toLocaleString("id-ID")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* SELECTED ITEMS */}
        <div className="mb-5">
          <h3 className="text-sm font-semibold mb-2 dark:text-gray-300">
            Selected Items
          </h3>
          {!selectedItems.length ? (
            <p className="text-sm text-gray-500">Belum ada item dipilih</p>
          ) : (
            <div className="space-y-2">
              {selectedItems.map((item) => (
                <div
                  key={item.product_id}
                  className="flex justify-between items-center border border-gray-200 dark:border-gray-700 rounded p-3"
                >
                  <div>
                    <div className="font-medium dark:text-gray-200">
                      {item.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Rp {Number(item.price).toLocaleString("id-ID")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      value={item.price}
                      onChange={(e) =>
                        updatePrice(item.product_id, Number(e.target.value))
                      }
                      className="w-24 px-2 py-1 border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    />

                    <input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) =>
                        updateQty(item.product_id, Number(e.target.value))
                      }
                      className="w-16 px-2 py-1 border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    />
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="text-red-500 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* OPTIONS (TAX) */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeTax}
              onChange={(e) => setIncludeTax(e.target.checked)}
              className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
            />
            <span className="text-sm font-medium dark:text-gray-200">
              Apply Tax ({taxRate}%)
            </span>
          </label>
        </div>

        {/* NOTES */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1 dark:text-gray-300">
            Notes
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
            placeholder="Optional notes..."
          />
        </div>

        {/* FOOTER / SUMMARY */}
        <div className="border-t pt-4 space-y-2 dark:border-gray-700">
          <div className="flex justify-between text-sm dark:text-gray-400">
            <span>Subtotal</span>
            <span>Rp {subtotal.toLocaleString("id-ID")}</span>
          </div>
          {includeTax && (
            <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
              <span>Tax ({taxRate}%)</span>
              <span>Rp {taxAmount.toLocaleString("id-ID")}</span>
            </div>
          )}
          <div className="flex justify-between items-center border-t pt-2 dark:border-gray-700">
            <div className="font-bold dark:text-white text-lg">Total</div>
            <div className="font-bold text-xl text-green-600">
              Rp {total.toLocaleString("id-ID")}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="px-6 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? "Processing..." : "Create Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
